import { useEffect, useState } from "react";
import {
  Cloud, Server, PlugZap, Database, RefreshCw, Loader2, CheckCircle2, XCircle,
  AlertTriangle, ShieldAlert, FileWarning, ListTree, Ban, Package, Download, PlayCircle, UserPlus, Upload,
} from "lucide-react";
import { adminApi } from "../../lib/api";
import { ConciliacionMensual } from "../../components/tsol/ConciliacionMensual";
import { TsolPaquetes, AlcanceBadge } from "../../components/tsol/TsolPaquetes";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const STATUS_COLORS = {
  PENDIENTE_SPEC: "bg-amber-100 text-amber-800 border-amber-200",
  BLOQUEADO_REGLA: "bg-red-100 text-red-800 border-red-200",
  REQUIERE_DECISION: "bg-blue-100 text-blue-800 border-blue-200",
  REVISION: "bg-amber-100 text-amber-800 border-amber-200",
  LISTO: "bg-emerald-100 text-emerald-800 border-emerald-200",
  PENDIENTE: "bg-red-100 text-red-800 border-red-200",
  DECISION: "bg-blue-100 text-blue-800 border-blue-200",
  CONST: "bg-slate-100 text-slate-700 border-slate-200",
  CALC: "bg-slate-100 text-slate-700 border-slate-200",
  OK: "bg-emerald-100 text-emerald-800 border-emerald-200",
  CONFIGURABLE: "bg-blue-100 text-blue-800 border-blue-200",
  CANDIDATO_FUERA_CONJUNTO: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminTsol() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState(null);
  const [rules, setRules] = useState(null);

  const [arcosRes, setArcosRes] = useState(null);
  const [ftpRes, setFtpRes] = useState(null);
  const [testingArcos, setTestingArcos] = useState(false);
  const [testingFtp, setTestingFtp] = useState(false);

  const [dateFrom, setDateFrom] = useState("2026-05-01");
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [executions, setExecutions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [genMode, setGenMode] = useState("test");
  const [skuReview, setSkuReview] = useState(null);
  const [pilotRes, setPilotRes] = useState(null);
  const [gpsRes, setGpsRes] = useState(null);
  const [busy, setBusy] = useState("");
  const [job, setJob] = useState(null);

  const [snapshots, setSnapshots] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [genFromSnap, setGenFromSnap] = useState("");
  const [cierreMeses, setCierreMeses] = useState([]);
  const [cierreMes, setCierreMes] = useState("");
  const [maxInvoices, setMaxInvoices] = useState(20);
  const [fileSets, setFileSets] = useState(null);
  const [opStatus, setOpStatus] = useState(null);
  const [ftpEvidence, setFtpEvidence] = useState(null);
  const [ftpBusy, setFtpBusy] = useState(false);

  const [mVendedores, setMVendedores] = useState([]);
  const [mMeta, setMMeta] = useState({});
  const [mLoading, setMLoading] = useState(false);
  const [histMode, setHistMode] = useState(false);
  const [s1Preview, setS1Preview] = useState(null);
  const [s1Busy, setS1Busy] = useState(false);

  const loadMasters = async () => {
    setMLoading(true);
    try {
      const { data } = await adminApi.get("/admin/tsol/masters/vendedores");
      setMVendedores(data.vendedores || []);
      setMMeta({ en_paquete_total: data.en_paquete_total, snapshot_id: data.snapshot_id });
    } catch (e) { /* noop */ } finally { setMLoading(false); }
  };

  const loadOpStatus = async () => {
    try {
      const { data } = await adminApi.get("/admin/tsol/operative-status");
      setOpStatus(data);
    } catch (e) { /* noop */ }
  };

  const prepareFtp = async () => {
    setFtpBusy(true);
    try {
      const { data } = await adminApi.post("/admin/tsol/prepare-ftp");
      setFtpEvidence(data);
      if (data.ok) toast.success(`FTP OK · raíz listada (${(data.root_listing || []).length} entradas) · sin crear nada`);
      else toast.error("FTP falló: " + (data.error || ""));
      await loadOpStatus();
    } catch (e) {
      toast.error("Error preparando FTP");
    } finally {
      setFtpBusy(false);
    }
  };

  const afterSend = async (data) => {
    setFtpRes(data);
    if (data.ok) {
      toast.success(`Enviado ${data.zip_name} a la raíz · tamaño verificado (${data.remote_size} bytes, ${data.size_verification})${data.fuente_factura_aprobada ? " · fuente FACTURA aprobada: la tarea de las 19:00 vuelve a operar sola" : ""}`);
    } else if (!data.needs_confirmation) {
      toast.error(`Envío no completado en paso "${data.failed_step || '?'}": ${data.ftp_code || ""} ${data.error || "reintentos agotados"}`.trim());
    }
    await loadOpStatus();
    await loadExecutions();
    try { const { data: st } = await adminApi.get("/admin/tsol/status"); setStatus(st); } catch (e) { /* noop */ }
  };

  const briefOf = (b) => (b ? `${b.zip_name} · fuente ${(b.fuente_ventas || "pedido").toUpperCase()} · ${b.size_bytes} bytes · sha ${(b.sha256 || "").slice(0, 8)}…` : "—");

  // Envía EXACTAMENTE la ejecución indicada (botón "Enviar este paquete" de cada tarjeta).
  const sendFtp = async (execId, brief) => {
    if (brief && !window.confirm(`Enviar este paquete por FTP?\n${briefOf(brief)}`)) return;
    setFtpBusy(true);
    try {
      const { data } = await adminApi.post("/admin/tsol/send-ftp", null, { params: { execution_id: execId } });
      await afterSend(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando por FTP");
    } finally {
      setFtpBusy(false);
    }
  };

  // "Reintentar envío": SIEMPRE la última ejecución validada (la resuelve el servidor, nunca la última enviada).
  const sendLatestValidated = async () => {
    setFtpBusy(true);
    try {
      const { data: op } = await adminApi.get("/admin/tsol/operative-status");
      setOpStatus(op);
      const latest = op?.ultima_validada;
      if (!latest) { toast.error("No hay ejecuciones validadas con ZIP inmutable."); return; }
      if (op?.validada_distinta_de_enviada) {
        const ok = window.confirm(`La última ejecución VALIDADA es distinta de la última ENVIADA.\n\nSe enviará la última validada:\n${briefOf(latest)}\n\nÚltima enviada:\n${briefOf(op.ultima_enviada)}\n\n¿Continuar?`);
        if (!ok) return;
      }
      const { data } = await adminApi.post("/admin/tsol/send-latest-validated", null, { params: { confirm_execution_id: latest.execution_id } });
      if (data.needs_confirmation) {
        toast.error("La última validada cambió mientras confirmabas; vuelve a pulsar Reintentar envío.");
        await loadOpStatus();
        return;
      }
      await afterSend(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando por FTP");
    } finally {
      setFtpBusy(false);
    }
  };


  const loadFileSets = async () => {
    try {
      const { data } = await adminApi.get("/admin/tsol/file-sets");
      setFileSets(data);
    } catch (e) { /* noop */ }
  };

  const loadSnapshots = async () => {
    try {
      const { data } = await adminApi.get("/admin/tsol/snapshots", { params: { limit: 10 } });
      setSnapshots(data.snapshots || []);
    } catch (e) { /* noop */ }
  };

  const runSnapshot = async () => {
    if (histMode) return runSnapshotHistorical();
    setSyncing(true);
    try {
      const { data } = await adminApi.post("/admin/tsol/snapshot", null, {
        params: { date_from: dateFrom, date_to: dateTo, max_invoices: maxInvoices },
      });
      const sid = data.snapshot_id;
      toast.info("Sincronizando ARCOS (snapshot acotado)...");
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: sn } = await adminApi.get(`/admin/tsol/snapshots/${sid}`);
        if (["listo", "fallido"].includes(sn.state)) {
          await loadSnapshots();
          if (sn.state === "fallido") toast.error("Snapshot falló: " + (sn.error || ""));
          else toast.success(`Snapshot listo · ${sn.metrics?.seleccionados} facturas seleccionadas · ${sn.metrics?.llamadas_arcos} llamadas a ARCOS`);
          break;
        }
      }
    } catch (e) {
      toast.error("Error sincronizando ARCOS");
    } finally {
      setSyncing(false);
    }
  };

  const runSnapshotHistorical = async () => {
    setSyncing(true);
    try {
      const { data } = await adminApi.post("/admin/tsol/snapshot", null, {
        params: { historical: true },
      });
      const sid = data.snapshot_id;
      toast.info(`Snapshot HISTÓRICO ${data.date_from} → ${data.date_to} (rango completo, sin tope). Puede tardar varios minutos...`);
      await loadSnapshots();
      for (let i = 0; i < 480; i++) { // ~40 min máx
        await new Promise((r) => setTimeout(r, 5000));
        const { data: sn } = await adminApi.get(`/admin/tsol/snapshots/${sid}`);
        if (i % 3 === 0) await loadSnapshots(); // refresca progreso en vivo cada ~15s
        if (["listo", "fallido"].includes(sn.state)) {
          await loadSnapshots(); await loadMasters();
          if (sn.state === "fallido") toast.error("Snapshot histórico falló: " + (sn.error || ""));
          else toast.success(`Snapshot histórico listo · ${sn.metrics?.seleccionados} facturas · ${sn.metrics?.llamadas_arcos} llamadas a ARCOS`);
          break;
        }
      }
    } catch (e) {
      toast.error("Error en snapshot histórico");
    } finally {
      setSyncing(false);
    }
  };

  const loadS1Preview = async () => {
    setS1Busy(true);
    try {
      const { data } = await adminApi.get("/admin/tsol/masters/vendedores/assign-s1/preview");
      setS1Preview(data);
    } catch (e) { toast.error("No se pudo cargar la vista previa de S1"); }
    finally { setS1Busy(false); }
  };

  const applyS1 = async () => {
    setS1Busy(true);
    try {
      const { data } = await adminApi.post("/admin/tsol/masters/vendedores/assign-s1");
      toast.success(`${data.asignados} vendedor(es) → ${data.supervisor?.code}`);
      setS1Preview(null);
      await loadMasters();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo asignar S1"); }
    finally { setS1Busy(false); }
  };


  const generateFromSnapshot = async (sid, alcance = "historico", mes = null) => {
    setGenFromSnap(`${sid}:${alcance}`);
    try {
      const { data } = await adminApi.post("/admin/tsol/generate-from-snapshot", null, {
        params: { snapshot_id: sid, alcance, ...(alcance === "cierre" && mes ? { mes } : {}) },
      });
      const eid = data.execution_id;
      toast.info(`Generando ZIP ${alcance.toUpperCase()}${data.ventana ? ` ${data.ventana.date_from} → ${data.ventana.date_to}` : ""} desde snapshot (sin llamar a ARCOS)...`);
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: ex } = await adminApi.get(`/admin/tsol/executions/${eid}`);
        if (["validado", "rechazado", "fallido"].includes(ex.state)) {
          await loadExecutions();
          if (ex.state === "fallido") toast.error("Generación falló: " + (ex.error || ""));
          else if (ex.state === "rechazado") toast.warning(`ZIP generado con ${ex.records_rejected} bloqueos (no enviable, no se inventan datos)`);
          else toast.success("ZIP validado");
          break;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando desde snapshot");
    } finally {
      setGenFromSnap("");
    }
  };


  const downloadReport = (eid) => {
    adminApi.get(`/admin/tsol/executions/${eid}/report`, { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a"); a.href = url; a.download = `reporte_${eid.slice(0,8)}.json`;
        document.body.appendChild(a); a.click(); a.remove();
      }).catch(() => toast.error("No se pudo descargar el reporte"));
  };
  const loadSkuReview = async () => {
    setBusy("sku");
    try { const { data } = await adminApi.get("/admin/tsol/sku-review"); setSkuReview(data); }
    catch (e) { toast.error("Error consultando SKU"); } finally { setBusy(""); }
  };
  const approveSku = async (id, tipo) => {
    try { await adminApi.post(`/admin/tsol/sku-review/${id}`, { tipo }); toast.success(`${id} → ${tipo}`); loadSkuReview(); }
    catch (e) { toast.error("No se pudo aprobar"); }
  };
  const runPilot = async () => {
    setBusy("pilot"); setPilotRes(null);
    try { const { data } = await adminApi.post("/admin/tsol/pilot", null, { params: { date_from: dateFrom, date_to: dateTo, limit: 100 } }); setPilotRes(data); toast.success("Piloto completado"); }
    catch (e) { toast.error("Piloto expiró en el navegador (puede seguir en segundo plano)"); } finally { setBusy(""); }
  };
  const runGps = async () => {
    setBusy("gps"); setGpsRes(null);
    try { const { data } = await adminApi.post("/admin/tsol/gps-audit", null, { params: { limit: 300 } }); setGpsRes(data); toast.success("Auditoría GPS lista"); }
    catch (e) { toast.error("Auditoría GPS expiró"); } finally { setBusy(""); }
  };
  const startHistorical = async () => {
    try {
      const { data } = await adminApi.post("/admin/tsol/job/historical", null, { params: { date_from: dateFrom, date_to: dateTo } });
      toast.info("Job histórico iniciado"); pollJob(data.job_id);
    } catch (e) { toast.error("No se pudo iniciar el job"); }
  };
  const jobAction = async (id, action) => {
    try { await adminApi.post(`/admin/tsol/job/${id}/${action}`); toast.info(`Job: ${action}`); }
    catch (e) { toast.error("Acción de job falló"); }
  };
  const pollJob = async (id) => {
    for (let i = 0; i < 400; i++) {
      try { const { data } = await adminApi.get(`/admin/tsol/job/${id}`); setJob(data);
        if (["completado", "cancelado", "fallido"].includes(data.state)) break;
      } catch (e) { /* keep polling */ }
      await new Promise((r) => setTimeout(r, 3000));
    }
  };


  const loadExecutions = async () => {
    try {
      const { data } = await adminApi.get("/admin/tsol/executions", { params: { limit: 10 } });
      setExecutions(data.executions || []);
    } catch (e) { /* noop */ }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, m, r] = await Promise.all([
        adminApi.get("/admin/tsol/status"),
        adminApi.get("/admin/tsol/mapping"),
        adminApi.get("/admin/tsol/rules"),
      ]);
      setStatus(s.data);
      setMapping(m.data);
      setRules(r.data);
    } catch (e) {
      toast.error("No se pudo cargar el estado de TSOL");
    } finally {
      setLoading(false);
    }
  };

  const loadCierreMeses = async () => {
    try {
      const { data } = await adminApi.get("/admin/tsol/cierre-meses");
      setCierreMeses(data.meses || []);
      setCierreMes((cur) => cur || data.meses?.[0]?.mes || "");
    } catch (e) { /* noop */ }
  };

  useEffect(() => { loadAll(); loadExecutions(); loadSnapshots(); loadFileSets(); loadOpStatus(); loadMasters(); loadCierreMeses(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await adminApi.post("/admin/tsol/generate", null, {
        params: { date_from: dateFrom, date_to: dateTo, mode: genMode },
      });
      const eid = data.execution_id;
      toast.info(`Generando paquete (${genMode})...`);
      // poll
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: ex } = await adminApi.get(`/admin/tsol/executions/${eid}`);
        if (["validado", "rechazado", "fallido"].includes(ex.state)) {
          await loadExecutions();
          if (ex.state === "fallido") toast.error("Generación falló: " + (ex.error || ""));
          else if (ex.state === "rechazado") toast.warning(`Paquete generado con ${ex.records_rejected} bloqueos (no enviable)`);
          else toast.success("Paquete validado");
          break;
        }
      }
    } catch (e) {
      toast.error("Error generando paquete");
    } finally {
      setGenerating(false);
    }
  };

  const downloadMatrix = (fmt) => {
    adminApi.get(`/admin/tsol/matrix/export`, { params: { format: fmt }, responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `matriz_arcos_tracksales.${fmt}`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => toast.error("No se pudo descargar la matriz"));
  };

  const downloadZip = (eid) => {
    adminApi.get(`/admin/tsol/executions/${eid}/download`, { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(executions.find((x) => x.id === eid)?.package?.zip_name) || eid + ".zip"}`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(async (e) => {
        let msg = "No se pudo descargar el ZIP";
        try { msg = JSON.parse(await e?.response?.data?.text())?.detail || msg; } catch { /* noop */ }
        toast.error(msg);
      });
  };

  const testArcos = async () => {
    setTestingArcos(true); setArcosRes(null);
    try {
      const { data } = await adminApi.post("/admin/tsol/test-arcos");
      setArcosRes(data);
      data.ok ? toast.success("Conexión ARCOS OK") : toast.error("ARCOS no respondió");
    } catch (e) { toast.error("Error probando ARCOS"); }
    finally { setTestingArcos(false); }
  };

  const testFtp = async () => {
    setTestingFtp(true); setFtpRes(null);
    try {
      const { data } = await adminApi.post("/admin/tsol/test-ftp");
      setFtpRes(data);
      data.ok ? toast.success("Conexión TSOL OK (protocolo pendiente de homologación)") : toast.error("FTP no respondió");
    } catch (e) { toast.error("Error probando FTP"); }
    finally { setTestingFtp(false); }
  };

  const consultarDatos = async () => {
    setLoadingPreview(true); setPreview(null);
    try {
      const { data } = await adminApi.get("/admin/tsol/preview", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setPreview(data);
      toast.success("Datos consultados en ARCOS");
    } catch (e) { toast.error("Error consultando datos"); }
    finally { setLoadingPreview(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-cm-ink-soft" data-testid="tsol-loading">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Cargando Integración TSOL...
      </div>
    );
  }

  const cfg = status?.config || {};
  const blocking = status?.blocking_rules || [];
  const fmtBogota = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("es-CO", { timeZone: "America/Bogota",
        year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return iso.slice(0, 16).replace("T", " "); }
  };

  return (
    <div className="space-y-6" data-testid="admin-tsol">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-2xl text-cm-ink flex items-center gap-2">
            <Cloud className="h-6 w-6" /> Integración TSOL
          </h1>
          <p className="text-sm text-cm-ink-soft mt-1">
            Orquestador ARCOS → TrackSales → ZIP → FTP TSOL · {status?.phase}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} data-testid="tsol-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
        </Button>
      </div>

      {/* Safety banner */}
      <div className={`rounded-lg border p-4 flex items-start gap-3 ${status?.upload_enabled ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}
           data-testid="tsol-safety-banner">
        {status?.upload_enabled
          ? <ShieldAlert className="h-5 w-5 text-red-600 mt-0.5" />
          : <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />}
        <div className="text-sm">
          <p className="font-semibold">
            Envío automático: {status?.upload_enabled ? "ACTIVADO" : "DESACTIVADO (UPLOAD_ENABLED=false)"}
          </p>
          <p className="text-cm-ink-soft">
            {status?.upload_enabled
              ? "El sistema PUEDE subir paquetes reales al FTP. Verifica reglas y validaciones."
              : "Modo seguro: solo Sincronizar ARCOS → generar ZIP desde snapshot → descargar. NO se envía nada a TSOL (sin FTP, sin scheduler)."}
          </p>
        </div>
      </div>

      {/* Blocking rules alert */}
      {blocking.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="tsol-blocking-alert">
          <p className="font-semibold text-red-800 flex items-center gap-2">
            <Ban className="h-4 w-4" /> {blocking.length} regla(s) PENDIENTE(S) que bloquean el envío
          </p>
          <ul className="list-disc ml-6 mt-1 text-sm text-red-700">
            {blocking.map((r) => <li key={r.key}>{r.titulo}: {r.observacion}</li>)}
          </ul>
        </div>
      )}

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="tsol-status-cards">
        <StatusCard title="Casillero / prefijo FTP" value={`${cfg.distributor_prefix}_${cfg.distributor_code}`}
                    sub="Nombre por confirmar con TSOL (no es 'código distribuidor')" icon={Server} />
        <StatusCard title="Formato de salida" value={`${(cfg.output_format || "").toUpperCase()} « ${cfg.delimiter} »`}
                    sub={cfg.output_format_homologated
                      ? "HOMOLOGADO · separador { · fechas DD-MM-AAAA · UTF-8 (correo TSOL 5-sep-2026)"
                      : "BORRADOR — NO HOMOLOGADO (encoding/CRLF/separador por confirmar con TSOL)"} icon={FileWarning} />
        <StatusCard title="Frecuencia requerida" value={status?.schedule_enabled ? "DIARIA · ACTIVA" : "DIARIA · DESACTIVADA"}
                    sub={status?.schedule_enabled
                      ? `Job daily_tsol_send ${status?.schedule_job_registered ? "registrado" : "NO registrado"} · próxima corrida: ${status?.next_run || "—"}`
                      : `TSOL_SCHEDULE_ENABLED=${status?.schedule_env ?? "false"} · job ${status?.schedule_job_registered ? "registrado pero pausado" : "no registrado"}`} icon={Cloud} />
        <StatusCard title="Manifiesto (conjunto de archivos)"
                    value={status?.manifest?.manifest_id || "—"}
                    sub={status?.conjunto_homologado ? `Homologado · ${status?.manifest?.files_ordered?.length} archivos` : `BORRADOR · ${status?.manifest?.files_ordered?.length} candidatos`} icon={ListTree} />
        <StatusCard title="Registros / bloqueos (última ejecución)"
                    value={`${status?.records_processed ?? 0} registros / ${status?.records_blocked ?? status?.records_rejected ?? 0} bloqueos`} icon={Database} />
        <StatusCard title="Errores registrados" value={status?.errors_total ?? 0}
                    sub={status?.last_error?.detail?.slice(0, 60)} icon={AlertTriangle} />
      </div>

      {/* Estado operativo (flujo definitivo) */}
      <div className="bg-white border border-cm-border rounded-lg p-5" data-testid="tsol-operative-status">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-serif text-lg flex items-center gap-2"><Cloud className="h-5 w-5" /> Estado operativo</h3>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={prepareFtp} disabled={ftpBusy} data-testid="tsol-prepare-ftp-btn">
              {ftpBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Server className="h-4 w-4 mr-1" />} Probar FTP (solo lectura: conectar y listar raíz)
            </Button>
            {opStatus?.ultima_validada?.execution_id && (
              <Button size="sm" onClick={sendLatestValidated}
                      disabled={ftpBusy || !opStatus?.upload_enabled || !opStatus?.ultima_validada?.can_send}
                      data-testid="tsol-send-ftp-btn" title={!opStatus?.upload_enabled ? "UPLOAD_ENABLED=false" : `Envía la última ejecución VALIDADA: ${opStatus.ultima_validada.zip_name}`}>
                <RefreshCw className="h-4 w-4 mr-1" /> Reintentar envío (última validada)
              </Button>
            )}
          </div>
        </div>
        {opStatus?.validada_distinta_de_enviada && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3" data-testid="tsol-validada-distinta">
            La última ejecución <b>validada</b> ({briefOf(opStatus.ultima_validada)}) es distinta de la última <b>enviada</b> ({briefOf(opStatus.ultima_enviada)}). "Reintentar envío" subirá la validada y pedirá confirmación.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="bg-cm-cream-alt rounded p-3" data-testid="op-sync">
            <div className="text-xs text-cm-ink-soft mb-1">Última sincronización ARCOS</div>
            <div className="font-medium">{opStatus?.ultima_sync_arcos ? <><code className="text-xs">{opStatus.ultima_sync_arcos.snapshot_id?.slice(0,8)}</code> · {fmtBogota(opStatus.ultima_sync_arcos.at)}</> : "—"}</div>
            <div className="text-xs text-cm-ink-soft">{opStatus?.ultima_sync_arcos?.llamadas_arcos != null ? `${opStatus.ultima_sync_arcos.llamadas_arcos} llamadas ARCOS` : ""}</div>
          </div>
          <div className="bg-cm-cream-alt rounded p-3" data-testid="op-zip">
            <div className="text-xs text-cm-ink-soft mb-1">Última ejecución VALIDADA (la que envía "Reintentar envío")</div>
            <div className="font-medium">{opStatus?.ultima_validada?.zip_name || "—"}</div>
            <div className="text-xs text-cm-ink-soft">{opStatus?.ultima_validada ? `fuente ${(opStatus.ultima_validada.fuente_ventas || "pedido").toUpperCase()} · ${opStatus.ultima_validada.size_bytes} bytes · sha256 ${(opStatus.ultima_validada.sha256 || "").slice(0, 12)}… · ${opStatus.ultima_validada.transfer_status || "no_enviado"}` : ""}</div>
            {opStatus?.ultima_enviada && <div className="text-[11px] text-cm-ink-soft mt-1">Última enviada: {opStatus.ultima_enviada.zip_name} · {opStatus.ultima_enviada.size_bytes} bytes · sha {(opStatus.ultima_enviada.sha256 || "").slice(0, 8)}…</div>}
          </div>
          <div className="bg-cm-cream-alt rounded p-3" data-testid="op-ftp">
            <div className="text-xs text-cm-ink-soft mb-1">Último envío FTP · archivo de hoy (raíz, sin carpeta)</div>
            <div className="font-medium" data-testid="op-zip-hoy">{opStatus?.zip_hoy}</div>
            <div className="text-xs">
              {opStatus?.ultimo_envio_ftp ? (
                <span className={opStatus.ultimo_envio_ftp.ok ? "text-emerald-700" : "text-red-600"}>
                  {opStatus.ultimo_envio_ftp.ok ? "enviado ✓" : `fallido ✗ (${opStatus.ultimo_envio_ftp.failed_step || "?"})`} · {opStatus.ultimo_envio_ftp.zip_name} · {fmtBogota(opStatus.ultimo_envio_ftp.at)}
                </span>
              ) : <span className="text-cm-ink-soft">sin envíos (UPLOAD_ENABLED={String(opStatus?.upload_enabled)})</span>}
            </div>
          </div>
        </div>
        <div className="mt-3 border border-cm-border rounded p-3 text-xs" data-testid="tsol-last-cron-run">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <span className="font-semibold">Tarea diaria TSOL · última corrida</span>
            <span className="text-cm-ink-soft">
              webhook externo: <code>{status?.cron_webhook_path || "/api/cron/tsol-daily"}</code> (cron-job.org 19:00 Bogotá) · respaldo interno: <code>daily_tsol_send</code>{status?.next_run ? ` · próxima ${status.next_run}` : " · pausado"}
            </span>
          </div>
          {status?.last_cron_run ? (
            <div className="flex flex-wrap gap-x-4 gap-y-0.5" data-testid="tsol-last-cron-run-detail">
              <span>{status.last_cron_run.started_at_bogota}</span>
              <span>origen: <b>{status.last_cron_run.trigger || "—"}</b></span>
              <span>estado: <b className={status.last_cron_run.state === "completado" ? "text-emerald-700" : status.last_cron_run.state === "running" ? "text-blue-700" : status.last_cron_run.state === "omitido" ? "text-amber-700" : "text-red-700"}>{status.last_cron_run.state}</b></span>
              <span>resultado: <b>{status.last_cron_run.result?.status || (status.last_cron_run.state === "running" ? "en curso" : "—")}</b></span>
              {status.last_cron_run.result?.zip_name && <span>ZIP: <b>{status.last_cron_run.result.zip_name}</b> · {status.last_cron_run.result.size_bytes} B · sha <code>{String(status.last_cron_run.result.sha256 || "").slice(0, 12)}…</code></span>}
              {status.last_cron_run.execution_id && <span>ejecución: <code>{status.last_cron_run.execution_id.slice(0, 8)}</code></span>}
              {status.last_cron_run.failed_step && <span className="text-red-700">paso: <b>{status.last_cron_run.failed_step}</b> · {status.last_cron_run.error}</span>}
              {!status.last_cron_run.failed_step && status.last_cron_run.error && <span className="text-amber-700">{status.last_cron_run.error}</span>}
            </div>
          ) : <p className="text-cm-ink-soft">Sin corridas registradas del job (ni interno ni webhook).</p>}
          {status?.last_cron_run?.result?.paquetes?.length > 0 && (
            <div className="mt-1.5 pl-2 border-l-2 border-cm-border" data-testid="tsol-last-cron-paquetes">
              <p className="text-[11px] text-cm-ink-soft mb-0.5">Paquetes de esa corrida:</p>
              <TsolPaquetes paquetes={status.last_cron_run.result.paquetes} testId="tsol-last-cron-paquetes-list" compact />
            </div>
          )}
          {status?.sent_today && <p className="text-emerald-700 mt-1" data-testid="tsol-sent-today">Diario de hoy confirmado: {status.sent_today.zip_name} · {status.sent_today.size} B · {fmtBogota(status.sent_today.at)}</p>}
        </div>
        {status?.plan_hoy && (
          <div className="mt-3 border border-cm-border rounded p-3 text-xs" data-testid="tsol-plan-hoy">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
              <span className="font-semibold">Paquetes de la corrida de hoy ({status.plan_hoy.fecha}, 19:00 Bogotá)</span>
              <span className="text-cm-ink-soft">
                diario = mes en curso (1 → hoy) · cierre = mes anterior completo el día {status.plan_hoy.cierre_dia} · idempotente por archivo + alcance
              </span>
            </div>
            <TsolPaquetes paquetes={status.plan_hoy.paquetes} testId="tsol-plan-hoy-list" />
            {status.plan_hoy.proximo_cierre && !status.plan_hoy.paquetes?.some((p) => p.alcance === "cierre") && (
              <p className="text-cm-ink-soft mt-1" data-testid="tsol-proximo-cierre">
                Próximo cierre: {status.plan_hoy.proximo_cierre.fecha_corrida} → <b>{status.plan_hoy.proximo_cierre.zip_name}</b> ({status.plan_hoy.proximo_cierre.date_from} → {status.plan_hoy.proximo_cierre.date_to}), antes del diario de ese día.
              </p>
            )}
            {status.cierre_pendiente && (
              <p className={`mt-1 font-medium ${status.cierre_pendiente.atrasado ? "text-red-700" : "text-amber-700"}`} data-testid="tsol-cierre-pendiente">
                {status.cierre_pendiente.atrasado ? "ALERTA · " : ""}{status.cierre_pendiente.mensaje}
                {status.cierre_pendiente.ultimo_error ? ` Último error: ${status.cierre_pendiente.ultimo_error}` : ""}
              </p>
            )}
          </div>
        )}
        {opStatus && !opStatus.upload_enabled && (
          <p className="text-xs text-amber-700 mt-2" data-testid="op-alert">Automatización DESACTIVADA · UPLOAD_ENABLED=false. Envío diario y reintentos se habilitan tras aprobar la prueba.</p>
        )}
        {ftpEvidence && (
          <div className="mt-3 text-xs border border-cm-border rounded p-3" data-testid="tsol-ftp-evidence">
            <p className="font-semibold mb-1">Evidencia FTP (prueba, sin subir archivo):</p>
            <p>Conexión: <b className={ftpEvidence.ok ? "text-emerald-700" : "text-red-600"}>{ftpEvidence.ok ? `OK (${ftpEvidence.mode})` : "FALLÓ"}</b> · {ftpEvidence.host}:{ftpEvidence.port}</p>
            {ftpEvidence.ok && <p>Raíz: <code>{ftpEvidence.remote_path || "/"}</code> · contenido: {JSON.stringify(ftpEvidence.root_listing)} · creado: nada · subido: no</p>}
            {ftpEvidence.error && <p className="text-red-600">{ftpEvidence.error}</p>}
          </div>
        )}
        {ftpRes && (
          <div className={`mt-3 text-xs border rounded p-3 ${ftpRes.ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="tsol-ftp-send-result">
            <p className="font-semibold mb-1">
              Último envío al FTP: <b className={ftpRes.ok ? "text-emerald-700" : "text-red-600"}>{ftpRes.ok ? "ENVIADO ✓" : "FALLÓ ✗"}</b>
              {ftpRes.intentos ? ` · intentos: ${ftpRes.intentos}` : ""}
            </p>
            {ftpRes.zip_name && <p>Destino: <b>raíz del casillero</b> ({ftpRes.remote_path || "/"}) · archivo: <b>{ftpRes.zip_name}</b>{ftpRes.sha256 ? <> · sha256 <code>{ftpRes.sha256.slice(0, 16)}…</code></> : null}</p>}
            <p>Verificación: <b>{ftpRes.size_verification || "—"}</b> · remoto: <b>{ftpRes.remote_size ?? "?"}</b> vs local: <b>{ftpRes.local_size ?? "?"}</b> bytes</p>
            {!ftpRes.ok && (
              <p className="text-red-700 mt-1">
                Paso que falló: <b>{ftpRes.failed_step || "?"}</b>
                {ftpRes.ftp_code ? ` · código FTP: ${ftpRes.ftp_code}` : ""}
                {ftpRes.ftp_message || ftpRes.error ? ` · ${ftpRes.ftp_message || ftpRes.error}` : ""}
              </p>
            )}
            {Array.isArray(ftpRes.steps) && ftpRes.steps.length > 0 && (
              <div className="mt-2">
                <p className="font-medium mb-0.5">Detalle por paso:</p>
                <ul className="space-y-0.5">
                  {ftpRes.steps.map((s, i) => (
                    <li key={i} data-testid={`tsol-ftp-step-${s.step}`}>
                      <span className={s.ok ? "text-emerald-700" : "text-red-600"}>{s.ok ? "✓" : "✗"}</span>{" "}
                      <b>{s.step}</b>{s.detail ? ` — ${s.detail}` : ""}
                      {s.ftp_code ? ` [${s.ftp_code}]` : ""}
                      {s.ftp_message ? ` ${s.ftp_message}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <ConciliacionMensual refreshKey={executions?.[0]?.id} fuente={status?.fuente} />

      {/* Conjunto de archivos del ZIP — configurable, no fijo */}
      <div className="bg-white border border-cm-border rounded-lg p-5" data-testid="tsol-filesets-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="font-serif text-lg flex items-center gap-2"><ListTree className="h-5 w-5" /> Conjunto de archivos del ZIP</h3>
          {fileSets && (
            <span className={`text-xs px-2 py-0.5 rounded border ${fileSets.homologated ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}
                  data-testid="tsol-filesets-homologated">
              {fileSets.homologated ? "Set homologado (TSOL_REQUIRED_FILES)" : "BORRADOR — no homologado"}
            </span>
          )}
        </div>
        <p className="text-xs text-cm-ink-soft mb-3">
          El listado NO está fijo en el código: se controla con la variable <code>TSOL_REQUIRED_FILES</code>. Los {fileSets?.candidatos_internos?.length || 11} archivos actuales son <strong>candidatos/borrador</strong>. La ausencia de un archivo completo <strong>no bloquea</strong> el envío hasta confirmar con TSOL cuáles exige para <code>NUTRIUM_215247</code>.
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-3 p-2 rounded border bg-emerald-50 border-emerald-200"
             data-testid="tsol-names-homologated">
          <span className="text-xs px-2 py-0.5 rounded border bg-emerald-100 text-emerald-800 border-emerald-300 font-medium">
            ENTREGA HOMOLOGADA (TSOL 11-sep-2026)
          </span>
          <span className="text-xs text-emerald-900">
            El ZIP se sube <strong>directamente en la raíz del casillero</strong> (sin carpetas) con nombre{" "}
            <code>{status?.config?.zip_name_pattern}</code> — tipología de carga <code>TSOL_TIPOLOGIA_CARGA={status?.config?.tipologia_carga}</code>.
            Archivo de hoy: <code data-testid="tsol-zip-hoy">{status?.config?.zip_hoy}</code>.
          </span>
        </div>
        {fileSets && (
          <>
            <div className="mb-3">
              <span className="text-xs text-cm-ink-soft">Archivos activos en el ZIP ({fileSets.active_required_files?.length}):</span>
              <div className="flex flex-wrap gap-1 mt-1" data-testid="tsol-active-files">
                {fileSets.active_required_files?.map((f) => (
                  <span key={f} className="text-xs px-2 py-0.5 rounded border bg-cm-cream-alt border-cm-border">{f}</span>
                ))}
              </div>
            </div>
            <p className="text-xs text-cm-ink-soft mb-2">{fileSets.comparison?.nota}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse" data-testid="tsol-filesets-table">
                <thead>
                  <tr className="text-left border-b border-cm-border">
                    <th className="py-1 pr-2">Fuente</th>
                    <th className="py-1 pr-2"># Archivos</th>
                    <th className="py-1 pr-2">Archivos / Observación</th>
                  </tr>
                </thead>
                <tbody>
                  {fileSets.comparison?.conjuntos?.map((c) => (
                    <tr key={c.fuente} className="border-b border-cm-border/50 align-top">
                      <td className="py-1.5 pr-2 font-medium whitespace-nowrap">{c.fuente}</td>
                      <td className="py-1.5 pr-2">{c.total}</td>
                      <td className="py-1.5 pr-2">
                        {c.archivos?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {c.archivos.map((a) => (
                              <span key={a} className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">{a}</span>
                            ))}
                          </div>
                        )}
                        <span className="text-cm-ink-soft">{c.observacion}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Estado de vendedores del paquete (SOLO LECTURA — se edita en 'Vendedores Arcos') */}
      <div className="bg-white border border-cm-border rounded-lg p-5" data-testid="tsol-masters-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="font-serif text-lg flex items-center gap-2"><ListTree className="h-5 w-5" /> Vendedores del paquete ({mMeta.en_paquete_total ?? mVendedores.length})</h3>
          <Button size="sm" variant="outline" onClick={loadMasters} disabled={mLoading} data-testid="tsol-masters-reload">
            {mLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Recargar
          </Button>
        </div>
        <p className="text-xs text-cm-ink-soft mb-4">
          Origen: <strong>maestro del portal</strong> (pantalla <em>Vendedores Arcos</em>). La <strong>cédula es el NIT</strong> de ese maestro y el
          <strong> supervisor</strong> son las columnas del mismo maestro. Esta vista es de <strong>solo lectura</strong>: para cargar cédula/NIT o
          supervisor, edita en <em>Vendedores Arcos</em>. No se usan valores de ejemplo; lo que falte <strong>bloquea</strong>.
        </p>
        <div className="mb-4 border border-cm-border rounded p-3 bg-cm-cream-alt/40" data-testid="tsol-s1-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-cm-ink">
              <strong>Asignación masiva de supervisor S1</strong> — solo a vendedores <strong>activos con venta de Nutrimenti</strong> que
              <strong> NO tienen supervisor</strong>. No sobreescribe asignaciones existentes.
            </p>
            <Button size="sm" variant="outline" onClick={loadS1Preview} disabled={s1Busy} data-testid="tsol-s1-preview-btn">
              {s1Busy && !s1Preview ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />} Ver a quién se asignará S1
            </Button>
          </div>
          {s1Preview && (
            <div className="mt-3 text-xs" data-testid="tsol-s1-preview">
              <p className="mb-1">
                Supervisor: <strong>{s1Preview.supervisor?.code}</strong> · {s1Preview.supervisor?.nombre || <span className="text-red-600">(sin nombre — defínelo primero)</span>}
                <span className="text-cm-ink-soft"> (fuente: {s1Preview.supervisor?.fuente})</span>
              </p>
              {s1Preview.total === 0 ? (
                <p className="text-emerald-700" data-testid="tsol-s1-none">No hay vendedores pendientes: todos los activos con venta de Nutrimenti ya tienen supervisor.</p>
              ) : (
                <>
                  <p className="mb-1">Se asignará a <strong>{s1Preview.total}</strong> vendedor(es):</p>
                  <ul className="list-disc ml-5 mb-2 max-h-40 overflow-y-auto" data-testid="tsol-s1-list">
                    {s1Preview.candidatos.map((c) => (
                      <li key={c.id}><span className="font-mono">{c.id}</span> · {c.nombre || "(sin nombre)"}</li>
                    ))}
                  </ul>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={applyS1} disabled={s1Busy || !s1Preview.supervisor?.nombre} data-testid="tsol-s1-apply-btn">
                      {s1Busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Confirmar y asignar S1
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setS1Preview(null)} data-testid="tsol-s1-cancel-btn">Cancelar</Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="tsol-vendedores-table">
            <thead>
              <tr className="text-left border-b border-cm-border">
                <th className="py-1 pr-2">Código</th>
                <th className="py-1 pr-2">Nombre</th>
                <th className="py-1 pr-2">Cédula (NIT)</th>
                <th className="py-1 pr-2">Supervisor</th>
                <th className="py-1 pr-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {mVendedores.map((v) => (
                <tr key={v.id} className={`border-b border-cm-border/50 ${v.bloqueado ? "bg-amber-50/40" : ""}`} data-testid={`tsol-vendedor-row-${v.id}`}>
                  <td className="py-1.5 pr-2 font-mono">{v.id}</td>
                  <td className="py-1.5 pr-2">{v.nombre || <span className="text-red-600">(no está en el maestro)</span>}</td>
                  <td className="py-1.5 pr-2 font-mono">
                    <span className="flex items-center gap-1">
                      {v.cedula || <span className="text-amber-700">—</span>}
                      {v.cedula_valida ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2">{v.supervisor_code ? `${v.supervisor_code} · ${v.supervisor_nombre || ""}` : <span className="text-amber-700">sin asignar</span>}</td>
                  <td className="py-1.5 pr-2">
                    <span className={`px-2 py-0.5 rounded border text-[11px] ${v.bloqueado ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-emerald-100 text-emerald-800 border-emerald-300"}`}
                          data-testid={`tsol-vendedor-estado-${v.id}`}>
                      {v.bloqueado ? v.estado : "OK"}
                    </span>
                  </td>
                </tr>
              ))}
              {mVendedores.length === 0 && (
                <tr><td colSpan={5} className="py-3 text-cm-ink-soft">Sin snapshot listo o sin vendedores en el paquete.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


      {/* Snapshot: Sincronizar ARCOS (desacoplado de la generación) */}
      <div className="bg-white border border-cm-border rounded-lg p-5" data-testid="tsol-snapshot-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="font-serif text-lg flex items-center gap-2"><Database className="h-5 w-5" /> Sincronizar ARCOS (snapshot)</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs font-medium px-2 py-1.5 rounded border border-cm-border bg-cm-cream-alt cursor-pointer" data-testid="tsol-historical-toggle">
              <input type="checkbox" checked={histMode} onChange={(e) => setHistMode(e.target.checked)}
                     className="accent-cm-ink" data-testid="tsol-historical-checkbox" />
              Modo histórico (1-jun → hoy) · pruebas y conciliación
            </label>
            {!histMode && <>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                     className="w-36 h-9" data-testid="tsol-date-from" />
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                     className="w-36 h-9" data-testid="tsol-date-to" />
              <label className="text-xs text-cm-ink-soft">Máx. facturas</label>
              <Input type="number" min={1} max={50} value={maxInvoices}
                     onChange={(e) => setMaxInvoices(Number(e.target.value) || 20)}
                     className="w-20 h-9" data-testid="tsol-snapshot-max" />
            </>}
            <Button size="sm" onClick={runSnapshot} disabled={syncing} data-testid="tsol-snapshot-btn">
              {syncing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sincronizando...</>
                : <><RefreshCw className="h-4 w-4 mr-1" /> {histMode ? "Sincronizar histórico" : "Sincronizar ARCOS"}</>}
            </Button>
          </div>
        </div>
        {histMode ? (
          <p className="text-xs text-cm-ink-soft mb-3" data-testid="tsol-historical-note">
            <strong>Modo histórico:</strong> trae de ARCOS <strong>todas</strong> las facturas y notas crédito del rango completo acumulado
            (1 de junio → hoy, Bogotá), <strong>sin tope</strong>, con re-consulta de los últimos 20 días. Es el <em>mismo snapshot</em>
            que usa la tarea nocturna; lo que cambia es la <strong>ventana que se escribe en el paquete</strong>: la noche genera el
            <strong> diario</strong> (mes en curso, 1 → hoy) y el día {status?.config?.cierre_dia ?? 3} el <strong>cierre</strong> del mes anterior.
            El ZIP «Histórico (pruebas)» (1-jun → hoy) se mantiene solo para pruebas y conciliación. Puede tardar varios minutos.
          </p>
        ) : (
        <p className="text-xs text-cm-ink-soft mb-3">
          Trae de ARCOS <strong>una sola vez</strong> hasta {maxInvoices} <strong>pedidos con <code>PedidoFacturaId</code></strong> más recientes (muestra parcial, más recientes primero) dentro del rango {dateFrom} → {dateTo}, más sólo los clientes, vendedores, SKU e inventario relacionados. Reutiliza la caché local. Excluye pedidos sin <code>PedidoFacturaId</code>. Las <strong>anulaciones NO son verificables</strong> en ARCOS. La generación del ZIP se hace luego <strong>exclusivamente</strong> desde este snapshot (sin volver a ARCOS).
        </p>
        )}
        {snapshots.length === 0 ? (
          <p className="text-sm text-cm-ink-soft">Aún no hay snapshots. Presiona “Sincronizar ARCOS”.</p>
        ) : (
          <div className="space-y-3" data-testid="tsol-snapshots">
            {snapshots.map((sn) => {
              const m = sn.metrics || {};
              const stateColor = sn.state === "listo" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : sn.state === "fallido" ? "bg-red-100 text-red-800 border-red-200"
                : "bg-slate-100 text-slate-700 border-slate-200";
              return (
                <div key={sn.id} className="border border-cm-border rounded p-3" data-testid={`tsol-snapshot-${sn.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <div className="text-sm">
                      <span className={`text-xs px-2 py-0.5 rounded border mr-2 ${stateColor}`}>{sn.state}</span>
                      <span className="text-cm-ink-soft">
                        <code className="text-[11px]">{sn.id?.slice(0, 8)}</code> · {fmtBogota(sn.created_at)} (Bogotá) · rango real {(sn.date_from_effective || sn.date_from) || "—"} → {(sn.date_to_effective || sn.date_to) || "—"}
                      </span>
                    </div>
                    {sn.state === "listo" && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Button size="sm" onClick={() => generateFromSnapshot(sn.id, "diario")} disabled={!!genFromSnap}
                                data-testid={`tsol-gen-diario-${sn.id}`} title="Mes en curso: del 1 al día de hoy (Bogotá). Es el paquete que sale cada noche.">
                          {genFromSnap === `${sn.id}:diario` ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generando...</> : <><Package className="h-4 w-4 mr-1" /> Generar diario (1 → hoy)</>}
                        </Button>
                        <span className="inline-flex items-center gap-1 border border-cm-border rounded h-9 pl-1.5 bg-white" data-testid={`tsol-cierre-selector-${sn.id}`}>
                          <select value={cierreMes} onChange={(e) => setCierreMes(e.target.value)}
                                  className="text-xs bg-transparent outline-none h-8" data-testid={`tsol-cierre-mes-${sn.id}`}
                                  title="Mes a cerrar (cualquier mes terminado desde junio 2026). El ZIP se nombra con el último día de ese mes y se puede enviar con «Enviar este paquete».">
                            {cierreMeses.map((m) => <option key={m.mes} value={m.mes}>{m.mes} · {m.zip_name}</option>)}
                          </select>
                          <Button size="sm" variant="outline" onClick={() => generateFromSnapshot(sn.id, "cierre", cierreMes)} disabled={!!genFromSnap || !cierreMes}
                                  data-testid={`tsol-gen-cierre-${sn.id}`} title="Mes completo (1 → último día), nombrado con su último día. El día 3 sale solo el del mes anterior.">
                            {genFromSnap === `${sn.id}:cierre` ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generando...</> : <>Generar cierre</>}
                          </Button>
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => generateFromSnapshot(sn.id, "historico")} disabled={!!genFromSnap}
                                data-testid={`tsol-gen-from-snapshot-${sn.id}`} title="Rango completo del snapshot (1-jun → hoy). Solo pruebas y conciliación; NO es lo que se envía.">
                          {genFromSnap === `${sn.id}:historico` ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generando...</> : <>Histórico (pruebas)</>}
                        </Button>
                      </div>
                    )}
                  </div>
                  {sn.state !== "listo" && sn.state !== "fallido" && (
                    <div className="text-[11px] text-cm-ink-soft mb-1 flex items-center gap-2" data-testid={`tsol-snapshot-progress-${sn.id}`}>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>
                        En curso · examinados {sn.progress?.examinados ?? m.pedidos_examinados_muestra ?? 0}
                        {sn.total_candidatos ? ` / ${sn.total_candidatos}` : ""} · seleccionadas {sn.progress?.seleccionados ?? m.seleccionados ?? 0}
                        {sn.progress?.fase ? ` · fase: ${sn.progress.fase}` : ""} · llamadas ARCOS {m.llamadas_arcos ?? 0}
                        {sn.heartbeat_at ? ` · último avance ${fmtBogota(sn.heartbeat_at)}` : ""}
                      </span>
                    </div>
                  )}
                  {sn.state === "listo" && (
                    <>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid={`tsol-snapshot-metrics-${sn.id}`}>
                      {[
                        ["Pedidos examinados (muestra)", m.pedidos_examinados_muestra],
                        ["Con PedidoFacturaId", m.con_pedidofacturaid_encontrados],
                        ["Facturas seleccionadas", m.seleccionados],
                        ["Sin PedidoFacturaId", m.pendientes_sin_pedidofacturaid],
                        ["Anulados", m.anulados_estado ?? "NO DETERMINABLE"],
                        ["Líneas obtenidas", m.lineas_obtenidas],
                        ["Llamadas a ARCOS", m.llamadas_arcos],
                        ["Reutilizado de caché", m.datos_reutilizados_cache],
                      ].map(([label, val]) => (
                        <div key={label} className="bg-cm-cream-alt rounded px-2 py-1.5 text-center">
                          <div className={`font-semibold text-cm-ink ${typeof val === "string" ? "text-[11px]" : "text-lg"}`}>{val ?? "—"}</div>
                          <div className="text-[10px] text-cm-ink-soft leading-tight">{label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="text-[11px] text-cm-ink-soft mt-2 space-y-0.5">
                      <p><b>Rango solicitado:</b> {m.rango_solicitado?.from} → {m.rango_solicitado?.to} · <b>Rango real de las seleccionadas:</b> {m.rango_real_seleccionadas?.from} → {m.rango_real_seleccionadas?.to}</p>
                      <p><b>Orden:</b> {m.orden} · verificado descendente: {m.orden_verificado_desc ? "sí" : "no"}</p>
                      {m.explicacion_rango && <p>{m.explicacion_rango}</p>}
                      {m.nota_conteo && <p className="italic">{m.nota_conteo}</p>}
                      {m.anulados_nota && <p className="italic">{m.anulados_nota}</p>}
                      {m.facturas_por_mes?.length > 0 && (
                        <div className="mt-1" data-testid={`tsol-snapshot-facturas-mes-${sn.id}`}>
                          <p><b>Recorrido Factura/Get por mes</b> (IDs {m.scan?.start_id} → {m.scan?.last_id} · inexistentes toleradas: {m.scan?.missing}):</p>
                          <table className="text-[11px] mt-0.5"><thead><tr className="text-cm-ink-soft"><th className="pr-3 text-left">Mes</th><th className="pr-3 text-right">Encontradas</th><th className="pr-3 text-right">Vigentes</th><th className="pr-3 text-right">Anuladas</th><th className="pr-3 text-right">Con Nutrimenti</th><th className="pr-3 text-right">Valor Nutrimenti</th><th className="pr-3 text-right">Directas s/pedido</th></tr></thead>
                          <tbody>{m.facturas_por_mes.map((f) => (
                            <tr key={f.mes}><td className="pr-3">{f.mes}</td><td className="pr-3 text-right">{f.encontradas}</td><td className="pr-3 text-right">{f.vigentes}</td><td className="pr-3 text-right">{f.anuladas}</td><td className="pr-3 text-right">{f.con_nutrimenti}</td><td className="pr-3 text-right">{Math.round(f.valor_nutrimenti).toLocaleString("es-CO")}</td><td className="pr-3 text-right">{f.directas_sin_pedido}</td></tr>
                          ))}</tbody></table>
                          {m.facturas_advertencias?.length > 0 && <p className="text-amber-700 font-semibold">⚠ {m.facturas_advertencias.join(" · ")}</p>}
                        </div>
                      )}
                    </div>
                    </>
                  )}
                  {sn.error && <p className="text-xs text-red-600 mt-1">{sn.error}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ejecuciones / Descargas (ZIP + reporte) */}
      <div className="bg-white border border-cm-border rounded-lg p-5" data-testid="tsol-generate-card">
        <h3 className="font-serif text-lg flex items-center gap-2 mb-3"><Package className="h-5 w-5" /> Ejecuciones · descargar ZIP / reporte</h3>
        <p className="text-xs text-cm-ink-soft mb-3">
          Cada ejecución se generó <strong>exclusivamente desde un snapshot</strong> (0 llamadas a ARCOS) y queda trazada por <code>snapshot_id</code> y <code>manifest_id</code>. Descarga el ZIP y su reporte de validación.
        </p>
        {executions.length === 0 ? (
          <p className="text-sm text-cm-ink-soft">Aún no hay ejecuciones. Genera un ZIP desde un snapshot.</p>
        ) : (
          <div className="space-y-2" data-testid="tsol-executions">
            {executions.map((ex) => (
              <div key={ex.id} className="border border-cm-border rounded p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm">
                    <span className={`text-xs px-2 py-0.5 rounded border mr-2 ${
                      ex.state === "validado" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                      : ex.state === "rechazado" ? "bg-amber-100 text-amber-800 border-amber-200"
                      : ex.state === "fallido" ? "bg-red-100 text-red-800 border-red-200"
                      : "bg-slate-100 text-slate-700 border-slate-200"}`}>{ex.state}</span>
                    <span className="text-cm-ink-soft">{fmtBogota(ex.started_at)} (Bogotá)</span>
                    {typeof ex.records_processed === "number" && (
                      <span className="ml-2">· {ex.records_processed} registros · {ex.records_rejected} bloqueos</span>
                    )}
                    <span className="ml-2 text-xs">· ARCOS: <b>{ex.arcos_calls ?? 0}</b> llamadas</span>
                    <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded border ${ex.fuente_ventas === "factura" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid={`tsol-exec-fuente-${ex.id}`}>
                      fuente {ex.fuente_ventas === "factura" ? "FACTURA" : "PEDIDO"}
                    </span>
                    <span className="ml-1.5"><AlcanceBadge alcance={ex.alcance || "historico"} testId={`tsol-exec-alcance-${ex.id}`} /></span>
                    {ex.ventana && <span className="ml-1.5 text-[11px] text-cm-ink-soft" data-testid={`tsol-exec-ventana-${ex.id}`}>ventana {ex.ventana.date_from} → {ex.ventana.date_to}</span>}
                  </div>
                  {ex.package?.zip_name && (
                    <div className="flex items-center gap-2">
                      {ex.state === "validado" && ex.zip_confiable && ex.alcance && ex.alcance !== "historico" && (
                        <Button size="sm" onClick={() => sendFtp(ex.id, { zip_name: ex.package.zip_name, fuente_ventas: ex.fuente_ventas, size_bytes: ex.package.size_bytes, sha256: ex.package.sha256 })}
                                disabled={ftpBusy || !opStatus?.upload_enabled || !ex.can_send}
                                data-testid={`tsol-send-exec-${ex.id}`} title={!opStatus?.upload_enabled ? "UPLOAD_ENABLED=false" : (!ex.can_send ? "can_send=false" : `Sube exactamente este ZIP (${ex.package.size_bytes} bytes)`)}>
                          <Upload className="h-4 w-4 mr-1" /> Enviar este paquete
                        </Button>
                      )}
                      {ex.zip_confiable ? (
                        <Button size="sm" variant="outline" onClick={() => downloadZip(ex.id)}
                                data-testid={`tsol-download-${ex.id}`}>
                          <Download className="h-4 w-4 mr-1" /> ZIP
                        </Button>
                      ) : (
                        <span className="text-[10px] px-2 py-1 rounded border bg-amber-100 text-amber-800 border-amber-300 font-medium"
                              data-testid={`tsol-zip-unreliable-${ex.id}`} title={ex.zip_nota}>ZIP no confiable / regenerar</span>
                      )}
                      <Button size="sm" variant="outline" onClick={() => downloadReport(ex.id)}
                              data-testid={`tsol-report-${ex.id}`}>
                        <Download className="h-4 w-4 mr-1" /> Reporte
                      </Button>
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-cm-ink-soft mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  {ex.snapshot_id && <span>snapshot_id: <code>{ex.snapshot_id.slice(0, 8)}</code></span>}
                  {ex.manifest_id && <span>manifest_id: <code>{ex.manifest_id}</code></span>}
                  {ex.rango_solicitado && <span>{ex.alcance ? "rango snapshot" : "rango solicitado"}: {ex.rango_solicitado.from} → {ex.rango_solicitado.to}</span>}
                  {(ex.date_from || ex.date_to) && <span>{ex.alcance ? "ventana del paquete" : "rango real"}: {ex.date_from || "—"} → {ex.date_to || "—"}</span>}
                  {ex.fecha_operativa_bogota && <span>fecha ZIP (Bogotá): {ex.fecha_operativa_bogota}</span>}
                  {ex.included_files && <span>archivos: {ex.included_files.length} incluidos{ex.excluded_files?.length ? ` · ${ex.excluded_files.length} excluidos` : ""}</span>}
                </div>
                {(ex.generated_at_utc || ex.generated_at_bogota) && (
                  <p className="text-[10px] text-cm-ink-soft">generado: {ex.generated_at_bogota} · UTC {ex.generated_at_utc?.slice(0,19)?.replace("T"," ")}</p>
                )}
                {ex.output_format_estado && (
                  <p className="text-[10px] text-amber-700">Formato: {ex.output_format_estado}</p>
                )}
                {ex.error && <p className="text-[11px] text-red-700 mt-1" data-testid={`tsol-exec-error-${ex.id}`}>{ex.error}</p>}
                {ex.zip_confiable && ex.package && (
                  <div className={`mt-2 border rounded p-2 text-[11px] ${ex.zip_evidencia?.consistente === false ? "border-red-300 bg-red-50" : "border-cm-border bg-cm-cream-alt/40"}`}
                       data-testid={`tsol-zip-evidence-${ex.id}`}>
                    <p className="font-semibold text-cm-ink mb-1">Paquete inmutable (bytes guardados en Mongo · lo que descarga el botón ZIP, lo que sube el FTP y lo que envía el job)</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                      <span>archivo: <b>{ex.package.zip_name}</b></span>
                      <span>tamaño: <b data-testid={`tsol-zip-size-${ex.id}`}>{ex.package.size_bytes}</b> bytes</span>
                      <span>sha256: <code data-testid={`tsol-zip-sha-${ex.id}`}>{ex.package.sha256}</code></span>
                    </div>
                    {ex.zip_evidencia?.ventas && (
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1" data-testid={`tsol-zip-ventas-${ex.id}`}>
                        <span>Ventas.csv: <b>{ex.zip_evidencia.ventas.lineas}</b> líneas</span>
                        <span>facturas distintas: <b>{ex.zip_evidencia.ventas.facturas_distintas}</b></span>
                        <span>clientes: <b>{ex.zip_evidencia.ventas.clientes_distintos}</b></span>
                        <span>vendedores: <b>{ex.zip_evidencia.ventas.vendedores_distintos}</b></span>
                        <span>rango real: <b>{ex.zip_evidencia.ventas.fecha_min || "—"} → {ex.zip_evidencia.ventas.fecha_max || "—"}</b></span>
                        <span>TotalesControl: <b>{ex.zip_evidencia.totales_control != null ? ex.zip_evidencia.totales_control.toLocaleString("es-CO", { minimumFractionDigits: 2 }) : "—"}</b></span>
                      </div>
                    )}
                    {ex.zip_evidencia?.inconsistencias?.length > 0 && (
                      <ul className="list-disc ml-5 mt-1 text-red-700" data-testid={`tsol-zip-inconsistent-${ex.id}`}>
                        {ex.zip_evidencia.inconsistencias.map((m, i) => <li key={i}>{m}</li>)}
                      </ul>
                    )}
                    {ex.transfer_status && ex.transfer_status !== "no_enviado" && (
                      <p className={`mt-1 ${ex.transfer_status === "enviado" ? "text-emerald-700" : "text-red-700"}`} data-testid={`tsol-transfer-${ex.id}`}>
                        Envío FTP: <b>{ex.transfer_status}</b>
                        {ex.transfer_result?.failed_step ? ` · paso: ${ex.transfer_result.failed_step}` : ""}
                        {ex.transfer_result?.ftp_code ? ` · código: ${ex.transfer_result.ftp_code}` : ""}
                        {ex.transfer_result?.error ? ` · ${ex.transfer_result.error}` : ""}
                        {ex.transfer_result?.remote_size != null ? ` · remoto ${ex.transfer_result.remote_size} vs local ${ex.transfer_result.local_size}` : ""}
                      </p>
                    )}
                  </div>
                )}
                {ex.stats?.cobertura_mensual && (
                  <div className={`mt-2 border rounded p-2 ${ex.cobertura_bloquea ? "border-red-300 bg-red-50" : "border-cm-border bg-cm-cream-alt/40"}`}
                       data-testid={`tsol-coverage-${ex.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-1 mb-1">
                      <span className="text-[11px] font-semibold text-cm-ink">Cobertura por mes (días Nutrimenti / días del negocio)</span>
                      {ex.cobertura_bloquea
                        ? <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-600 text-white" data-testid={`tsol-coverage-block-${ex.id}`}>NO ENVIAR · mes(es) en rojo</span>
                        : <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">Cobertura OK</span>}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[10px] border-collapse" data-testid={`tsol-coverage-table-${ex.id}`}>
                        <thead>
                          <tr className="text-left text-cm-ink-soft border-b border-cm-border/60">
                            <th className="py-1 pr-2">Mes</th>
                            <th className="py-1 pr-2">Días Nutri / Negocio</th>
                            <th className="py-1 pr-2">Cobertura</th>
                            <th className="py-1 pr-2">Semáforo</th>
                            <th className="py-1 pr-2">Valor</th>
                            <th className="py-1 pr-2">Costo</th>
                            <th className="py-1 pr-2">Pedidos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ex.stats.cobertura_mensual.por_mes.map((mo) => {
                            const dot = mo.semaforo === "verde" ? "bg-emerald-500" : mo.semaforo === "amarillo" ? "bg-amber-500" : mo.semaforo === "rojo" ? "bg-red-600" : "bg-slate-300";
                            const lbl = mo.semaforo === "sin_datos" ? "sin datos" : mo.semaforo;
                            return (
                              <tr key={mo.mes} className="border-b border-cm-border/30" data-testid={`tsol-coverage-row-${mo.mes}`}>
                                <td className="py-1 pr-2 font-medium">{mo.mes}</td>
                                <td className="py-1 pr-2">{mo.dias_nutrimenti} / {mo.dias_negocio}</td>
                                <td className="py-1 pr-2">{mo.cobertura != null ? `${Math.round(mo.cobertura * 100)}%` : "—"}</td>
                                <td className="py-1 pr-2"><span className="inline-flex items-center gap-1"><span className={`inline-block h-2 w-2 rounded-full ${dot}`} />{lbl}</span></td>
                                <td className="py-1 pr-2">${Math.round(mo.valor).toLocaleString("es-CO")}{mo.bajo_piso && <span className="text-amber-700" title="Bajo el piso de advertencia"> ⚠</span>}</td>
                                <td className="py-1 pr-2">${Math.round(mo.costo).toLocaleString("es-CO")}</td>
                                <td className="py-1 pr-2">{mo.pedidos}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[10px] text-cm-ink-soft mt-1 italic">{ex.stats.cobertura_mensual.criterio}</p>
                  </div>
                )}
                {ex.proveedor_evidencia && (
                  <div className="text-[10px] mt-1 border border-emerald-200 bg-emerald-50 rounded p-2" data-testid={`tsol-proveedor-${ex.id}`}>
                    <p className="font-semibold text-emerald-800">Filtro proveedor Nutrimenti — {ex.proveedor_evidencia.filtro_aplicado ? "APLICADO" : "NO aplicado"}</p>
                    {ex.proveedor_evidencia.provider_rule_id && (
                      <p>Regla: <b>{ex.proveedor_evidencia.provider_rule_id}</b> · valor fuente: <b>{ex.proveedor_evidencia.provider_raw_value}</b> · {ex.proveedor_evidencia.comparacion}</p>
                    )}
                    <p>Origen: {ex.proveedor_evidencia.campo_origen}</p>
                    <p>SKU: {ex.proveedor_evidencia.sku_incluidos}/{ex.proveedor_evidencia.sku_snapshot_total} · Líneas ventas: {ex.proveedor_evidencia.lineas_ventas_incluidas} · Documentos: {ex.proveedor_evidencia.documentos_incluidos}/{ex.proveedor_evidencia.documentos_total} · Inventario: {ex.proveedor_evidencia.inventario_incluido} · Clientes: {ex.proveedor_evidencia.clientes_incluidos} · Vendedores: {ex.proveedor_evidencia.vendedores_incluidos}</p>
                    <p className="italic">{ex.proveedor_evidencia.codigo_nota} {ex.proveedor_evidencia.nota}</p>
                  </div>
                )}
                {ex.included_files && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {ex.included_files.map((f) => (
                      <span key={f} className="text-[10px] px-1.5 py-0.5 rounded border bg-cm-cream-alt border-cm-border">{f}</span>
                    ))}
                    {ex.excluded_files?.map((f) => (
                      <span key={f} className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-50 border-slate-200 text-slate-500 line-through">{f}</span>
                    ))}
                  </div>
                )}
                {ex.bloqueos_categorizados && (
                  <div className="text-[10px] text-cm-ink-soft mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-4">
                    {[
                      ["Estructural (informativo, no bloquea)", ex.bloqueos_categorizados.bloqueo_estructural_informativo],
                      ["Decisiones de negocio", ex.bloqueos_categorizados.decisiones_de_negocio],
                      ["Registros inválidos", ex.bloqueos_categorizados.registros_invalidos],
                      ["Advertencias", ex.bloqueos_categorizados.advertencias],
                      ["Errores técnicos", ex.bloqueos_categorizados.errores_tecnicos],
                    ].map(([label, arr]) => (arr && arr.length > 0) ? (
                      <div key={label} className="mt-1">
                        <span className="font-semibold text-cm-ink">{label} ({arr.length}):</span>
                        <ul className="list-disc ml-4">{arr.map((x, i) => <li key={i}>{x}</li>)}</ul>
                      </div>
                    ) : null)}
                  </div>
                )}
                {ex.error && <p className="text-xs text-red-600 mt-1">{ex.error}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Business rules */}
      <div className="bg-white border border-cm-border rounded-lg p-5" data-testid="tsol-rules-card">
        <h3 className="font-serif text-lg flex items-center gap-2 mb-3"><ShieldAlert className="h-5 w-5" /> Reglas de negocio</h3>
        <div className="space-y-2">
          {rules?.business_rules?.map((r) => (
            <div key={r.key} className="border border-cm-border rounded p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-medium">{r.titulo}</span>
                <span className="flex items-center gap-1">
                  {r.homologado_con && (
                    <span className="text-xs px-2 py-0.5 rounded border bg-emerald-50 text-emerald-800 border-emerald-200" data-testid={`tsol-rule-homologada-${r.key}`}>
                      HOMOLOGADA · {r.homologado_con}
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_COLORS[r.estado] || ""}`}>
                    {r.estado}{r.bloquea_envio && r.estado === "PENDIENTE" ? " · bloquea envío" : ""}
                  </span>
                </span>
              </div>
              <p className="text-sm text-cm-ink-soft mt-1">{r.regla}</p>
              <p className="text-xs text-cm-ink-soft mt-1"><b>Fuente Arcos:</b> {r.arcos_source}</p>
              <p className="text-xs text-cm-ink-soft"><b>Obs:</b> {r.observacion}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Mapping matrix */}
      <div className="bg-white border border-cm-border rounded-lg p-5" data-testid="tsol-mapping-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <h3 className="font-serif text-lg flex items-center gap-2"><ListTree className="h-5 w-5" /> Matriz ARCOS → TrackSales</h3>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" data-testid="tsol-matrix-csv"
                    onClick={() => downloadMatrix("csv")}><Download className="h-4 w-4 mr-1" /> CSV</Button>
            <Button size="sm" variant="outline" data-testid="tsol-matrix-json"
                    onClick={() => downloadMatrix("json")}><Download className="h-4 w-4 mr-1" /> JSON</Button>
          </div>
        </div>
        <p className="text-xs text-cm-ink-soft mb-3">
          Spec: {mapping?.spec_version}. Los archivos <strong>fuera del conjunto activo</strong> aparecen como <em>CANDIDATO — FUERA DEL CONJUNTO</em> (no generan bloqueos del paquete). Solo los campos de archivos incluidos bloquean el envío.
        </p>
        <div className="space-y-4">
          {mapping && Object.values(mapping.files).map((f) => (
            <details key={f.file} className="border border-cm-border rounded" data-testid={`tsol-file-${f.file}`}>
              <summary className="cursor-pointer px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                <span className="font-medium">{f.file}.csv
                  <span className="text-xs text-cm-ink-soft font-normal ml-2">({(f.columns || []).length} columnas)</span>
                </span>
                <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_COLORS[f.status] || ""}`}>
                  {f.status === "CANDIDATO_FUERA_CONJUNTO" ? "CANDIDATO — FUERA DEL CONJUNTO" : f.status}
                </span>
              </summary>
              <div className="overflow-x-auto border-t border-cm-border">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-cm-ink-soft border-b border-cm-border/60">
                      <th className="py-1.5 px-2">#</th>
                      <th className="py-1.5 px-2">Campo TrackSales</th>
                      <th className="py-1.5 px-2">Tipo</th>
                      <th className="py-1.5 px-2">Obl.</th>
                      <th className="py-1.5 px-2">Fuente ARCOS</th>
                      <th className="py-1.5 px-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(f.columns || []).map((col) => (
                      <tr key={col.pos} className="border-b border-cm-border/30 align-top">
                        <td className="py-1.5 px-2">{col.pos}</td>
                        <td className="py-1.5 px-2 font-medium">{col.campo}</td>
                        <td className="py-1.5 px-2 text-cm-ink-soft">{col.tipo}</td>
                        <td className="py-1.5 px-2">{col.obligatorio ? "S" : "N"}</td>
                        <td className="py-1.5 px-2 font-mono">{col.arcos}{col.obs ? <span className="block text-cm-ink-soft italic">{col.obs}</span> : null}</td>
                        <td className="py-1.5 px-2">
                          <span className={`px-1.5 py-0.5 rounded border ${STATUS_COLORS[col.estado] || ""}`}>{col.estado}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

const StatusCard = ({ title, value, sub, icon: Icon }) => (
  <div className="bg-white border border-cm-border rounded-lg p-4">
    <div className="flex items-center gap-2 text-cm-ink-soft text-xs uppercase tracking-wide">
      {Icon && <Icon className="h-4 w-4" />} {title}
    </div>
    <p className="text-lg font-serif mt-1 break-words">{value}</p>
    {sub && <p className="text-xs text-cm-ink-soft mt-0.5 break-words">{sub}</p>}
  </div>
);
